import React from 'react'
import { Bar, BarProps, Cell, Customized } from 'recharts'
import { customSpendBarShape } from 'src/components/SpendGraph/CustomBarShape'
import {
  HoverData,
  SpendGraphData,
  SpendType,
} from 'src/components/SpendGraph/SpendGraph'

interface SpendBarStackProps {
  currency: string
  graphData: SpendGraphData[]
  barProps?: {
    fill?: string
    radius?: number | [number, number, number, number]
    size?: number
  }
  onMouseEnter: (
    data: HoverData,
    index: number,
    spendType: SpendType,
    currency: string | undefined,
  ) => void
  onMouseLeave: (data: HoverData) => void
  onClick?: (data: unknown) => void
  hoverIndex: number
}

const spendBarStack = (props: SpendBarStackProps): React.ReactElement[] =>
  [
    <Bar
      key={`${props.currency}Physical`}
      dataKey={`${props.currency}Physical`}
      stackId={`${props.currency}Stack`}
      cursor={props.onClick && 'pointer'}
      barSize={props.barProps?.size}
      onMouseEnter={(data: HoverData, index: number): void => {
        props.onMouseEnter(data, index, SpendType.physical, props.currency)
      }}
      onMouseOut={props.onMouseLeave}
      onClick={(data): void => props.onClick?.(data)}
      // @ts-expect-error - BarProps does have a payload
      shape={(
        barProps: BarProps & { payload: SpendGraphData },
      ): JSX.Element => (
        <Customized
          component={customSpendBarShape({
            ...barProps,
            barRadius: 5,
            gap: 2,
            currency: props.currency,
            type: SpendType.physical,
          })}
        />
      )}
    >
      {props.graphData.map((entry, index) =>
        renderCell(entry.fillPhysical, index, props.hoverIndex),
      )}
    </Bar>,
    <Bar
      key={`${props.currency}BillPay`}
      dataKey={`${props.currency}BillPay`}
      stackId={`${props.currency}Stack`}
      cursor={props.onClick && 'pointer'}
      barSize={props.barProps?.size}
      onMouseEnter={(data: HoverData, index: number): void => {
        props.onMouseEnter(data, index, SpendType.billPay, props.currency)
      }}
      onMouseOut={props.onMouseLeave}
      onClick={(data): void => props.onClick?.(data)}
      // @ts-expect-error - BarProps does have a payload
      shape={(
        barProps: BarProps & { payload: SpendGraphData },
      ): JSX.Element => (
        <Customized
          component={customSpendBarShape({
            ...barProps,
            barRadius: 5,
            gap: 2,
            currency: props.currency,
            type: SpendType.billPay,
          })}
        />
      )}
    >
      {props.graphData.map((entry, index) =>
        renderCell(entry.fillBillPay, index, props.hoverIndex),
      )}
    </Bar>,
    <Bar
      key={`${props.currency}Virtual`}
      dataKey={`${props.currency}`}
      stackId={`${props.currency}Stack`}
      cursor={props.onClick && 'pointer'}
      barSize={props.barProps?.size}
      onMouseEnter={(data: HoverData, index: number): void => {
        props.onMouseEnter(data, index, SpendType.virtual, props.currency)
      }}
      onMouseOut={props.onMouseLeave}
      onClick={(data): void => props.onClick?.(data)}
      // @ts-expect-error - BarProps does have a payload
      shape={(
        barProps: BarProps & { payload: SpendGraphData },
      ): JSX.Element => (
        <Customized
          component={customSpendBarShape({
            ...barProps,
            barRadius: 5,
            gap: 2,
            currency: props.currency,
            type: SpendType.virtual,
          })}
        />
      )}
    >
      {props.graphData.map((entry, index) =>
        renderCell(entry.fill, index, props.hoverIndex),
      )}
    </Bar>,
  ].filter((item): item is React.ReactElement => !!item) as React.ReactElement[]

const renderCell = (
  fill: string | undefined,
  index: number,
  hoverIndex: number,
): React.ReactElement => (
  <Cell
    key={`cell-${index}`}
    fillOpacity={index === hoverIndex || hoverIndex === -1 ? 1.0 : 0.5}
    fill={fill}
  />
)

export default spendBarStack
